import { TableProps } from "../components/interpretation/Table";
import { InterpretationLevel } from "../routes/test/interpretation";
import { TableConfiguration, TestGraph } from "../types";
import {
    INTERPRETATION_CANCEL_ENDPOINT,
    INTERPRETATION_CONFIRM_ENDPOINT,
    INTERPRETATION_ENDPOINT,
    INTERPRETATION_GRAPHS_ENDPOINT,
    INTERPRETATION_TABLE_ENDPOINT,
} from "./endpoints";
import {
    convertDynamicLoopsData,
    convertPeakValuesData,
    convertPostSpirometryData,
    convertPreSpirometryData,
    convertResponsesData,
} from "./summary-tables";
import {
    InterpretationDataFromAPI,
    InterpretationTablesFromAPI,
    Nullable,
} from "./types";
import { fetcher } from "./utils";

export const getInterpretationData = async ({
    testId,
    refresh = false,
}: {
    testId: Nullable<string>;
    refresh?: boolean;
}) => {
    try {
        if (!testId) {
            return;
        }
        const response = (await fetcher(INTERPRETATION_ENDPOINT, {
            params: {
                test_id: testId,
                refresh,
            },
        })) as InterpretationDataFromAPI;
        return response;
    } catch (e) {
        console.error(e);
        return;
    }
};

export const updateInterpretationData = async ({
    testId,
    data,
}: {
    testId: Nullable<string>;
    data: Partial<InterpretationDataFromAPI>;
}) => {
    try {
        if (!testId) {
            return;
        }

        const response = await fetcher(INTERPRETATION_ENDPOINT, {
            method: "PUT",
            params: {
                test_id: testId,
            },
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        });

        return response;
    } catch (e) {
        console.error(e);
        return;
    }
};

export const getInterpretationGraphs = async ({
    testId,
    interpretationLevel,
}: {
    testId: Nullable<string>;
    interpretationLevel: InterpretationLevel;
}) => {
    try {
        if (!testId) {
            return;
        }

        const response = await fetcher(INTERPRETATION_GRAPHS_ENDPOINT, {
            params: {
                test_id: testId,
                interpretation_level: interpretationLevel,
            },
        });

        if (!response) return null;

        return response as TestGraph[];
    } catch (e) {
        console.error(e);
        return;
    }
};

export const getInterpretationTables = async ({
    testId,
    interpretationLevel,
}: {
    testId: Nullable<string>;
    interpretationLevel: InterpretationLevel;
}) => {
    try {
        if (!testId) {
            return;
        }

        const response = (await fetcher(INTERPRETATION_TABLE_ENDPOINT, {
            params: {
                test_id: testId,
                interpretation_level: interpretationLevel,
            },
        })) as InterpretationTablesFromAPI;

        const {
            final_peak_values,
            slopes,
            fv_loops,
            pre_spirometry,
            post_spirometry,
            responses,
            symptoms,
            person,
        } = response;

        let tables: TableProps[] = [];
        if (final_peak_values) {
            tables.push({
                id: "final_peak_values",
                title: "Peak Values",
                data: convertPeakValuesData(Object.values(final_peak_values)),
                configuration: TableConfiguration.INTERPRETATION,
            });
        }

        if (slopes) {
            tables.push({
                id: "slopes",
                title: "Slopes",
                data: convertPeakValuesData(Object.values(slopes)),
                configuration: TableConfiguration.SLOPES,
            });
        }

        if (fv_loops) {
            tables.push({
                id: "fv_loops",
                title: "Dynamic Loops",
                data: convertDynamicLoopsData(Object.values(fv_loops)),
                configuration: TableConfiguration.DYNAMIC_LOOP,
            });
        }

        if (pre_spirometry) {
            tables.push({
                id: "pre_spirometry",
                title: "Pre Spirometry",
                data: convertPreSpirometryData(Object.values(pre_spirometry)),
                configuration: TableConfiguration.PRE_SPIROMETRY,
            });
        }

        if (post_spirometry) {
            tables.push({
                id: "post_spirometry",
                title: "Post Spirometry",
                data: convertPostSpirometryData(Object.values(post_spirometry)),
                configuration: TableConfiguration.POST_SPIROMETRY,
            });
        }

        if (responses) {
            tables.push({
                id: "responses",
                title: "Responses",
                data: convertResponsesData(Object.values(responses)),
                configuration: TableConfiguration.RESPONSES,
            });
        }

        if (person) {
            tables.push({
                id: "person",
                title: "Personal Data",
                data: [
                    {
                        parameterName: "BMI",
                        measured: person.bmi,
                    },
                ],
                configuration: TableConfiguration.PERSON,
            });
        }

        return {
            tables,
            withSymptoms: Boolean(symptoms?.type),
            withECG: Boolean(symptoms?.rest_ecg || symptoms?.exercise_ecg),
        };
    } catch (e) {
        console.error(e);
        return;
    }
};

export const confirmInterpretation = async (testId?: string) => {
    try {
        if (!testId) {
            return;
        }

        const response = await fetcher(INTERPRETATION_CONFIRM_ENDPOINT, {
            method: "PUT",
            params: {
                test_id: testId,
            },
        });

        return response;
    } catch (e) {
        console.error(e);
        return;
    }
};

export const cancelInterpretation = async (testId?: string) => {
    try {
        if (!testId) {
            return;
        }

        const response = await fetcher(INTERPRETATION_CANCEL_ENDPOINT, {
            method: "PUT",
            params: {
                test_id: testId,
            },
        });

        return response;
    } catch (e) {
        console.error(e);
        return;
    }
};
